import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

/**
 * Knowledge — Total Recall UI tab.
 *
 * Four modes:
 *   - "Ask"     → /api/brain/recall (search-and-synthesize, prose answer)
 *   - "Brief"   → /api/brain/brief?topic=... (200-word topic briefing)
 *   - "Today"   → /api/brain/since (what changed in the last day)
 *   - "Ingest"  → /api/brain/ingest/* (Phase 6 write surface — drop files,
 *                 review LLM proposals, approve to commit to brain)
 */

const API = process.env.REACT_APP_API_URL || '';

const MODES = [
  { key: 'ask',    label: 'Ask',    placeholder: 'Ask the brain anything…',     path: '/brain/recall', method: 'POST' },
  { key: 'brief',  label: 'Brief',  placeholder: 'Topic to brief on…',           path: '/brain/brief',  method: 'GET'  },
  { key: 'today',  label: 'Today',  placeholder: '(no input — shows last 24h)', path: '/brain/since',  method: 'GET'  },
  { key: 'ingest', label: 'Ingest', placeholder: '',                             path: null,            method: null   },
];

const THRESHOLD_KEY = 'tr.ingest.autoCommitThreshold';
const COMMITTABLE_ACTIONS = ['create', 'append', 'update'];

const STATUS_FILTERS = [
  { key: 'all',     label: 'All',          match: () => true },
  { key: 'review',  label: 'Needs review', match: (p) => p.status === 'pending' || p.status === 'proposed' },
  { key: 'done',    label: 'Committed',    match: (p) => p.status === 'committed' },
  { key: 'failed',  label: 'Failed',       match: (p) => p.status === 'failed' },
  { key: 'skipped', label: 'Skipped',      match: (p) => p.status === 'skipped' },
];

function CitationChip({ slug }) {
  return (
    <span
      className="inline-block rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 mr-1.5 mb-1 text-[11px] font-mono text-slate-700"
      title={slug}
    >
      {slug}
    </span>
  );
}

function Mode({ active, mode, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
        active ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      {mode.label}
    </button>
  );
}

function ConfidencePill({ value }) {
  if (value == null) return <span className="text-slate-400 text-xs">…</span>;
  const pct = Math.round(value * 100);
  const color =
    value >= 0.95 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
    value >= 0.7  ? 'bg-amber-100 text-amber-800 border-amber-200' :
                    'bg-rose-100 text-rose-800 border-rose-200';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-mono border ${color}`}>
      {pct}%
    </span>
  );
}

function ActionBadge({ action }) {
  const map = {
    create: 'bg-indigo-100 text-indigo-800',
    append: 'bg-sky-100 text-sky-800',
    update: 'bg-violet-100 text-violet-800',
    defer:  'bg-slate-200 text-slate-700',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${map[action] || 'bg-slate-200 text-slate-700'}`}>
      {action || '—'}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   'bg-slate-100 text-slate-600 border-slate-200',
    proposed:  'bg-blue-50 text-blue-800 border-blue-200',
    committed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    skipped:   'bg-rose-50 text-rose-700 border-rose-200',
    failed:    'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-medium border ${map[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {status}
    </span>
  );
}

function IngestPanel() {
  // Activity feed: proposals are sorted newest-first by the backend, default
  // includes ALL statuses from the last 24h. The user sees outcomes (committed,
  // failed, skipped) alongside in-flight rows (pending, proposed). No outcome
  // is ever invisible — that's the design rule. Filter chips narrow the view
  // without losing data.
  const [proposals, setProposals] = useState([]);
  const [filterKey, setFilterKey] = useState('all');
  const [checked, setChecked] = useState({});       // { [id]: bool }
  const [overrides, setOverrides] = useState({});   // { [id]: { slug?, action? } }
  const [drawerId, setDrawerId] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [threshold, setThreshold] = useState(() => {
    const v = parseFloat(localStorage.getItem(THRESHOLD_KEY));
    return Number.isFinite(v) ? v : 0.95;
  });
  // Track which rows have been auto-checked once so unchecking sticks across polls.
  const autoCheckedRef = useRef(new Set());

  // Sync threshold from server on mount; localStorage wins on later sessions.
  useEffect(() => {
    fetch(`${API}/brain/ingest/threshold`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (typeof d.auto_commit_threshold === 'number') {
          const stored = parseFloat(localStorage.getItem(THRESHOLD_KEY));
          if (!Number.isFinite(stored)) {
            setThreshold(d.auto_commit_threshold);
            localStorage.setItem(THRESHOLD_KEY, String(d.auto_commit_threshold));
          }
        }
      })
      .catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    try {
      // No status filter — backend defaults to all statuses, last 24h. The
      // UI is an activity feed, not a backlog: a row should not vanish just
      // because it raced through a transient state faster than the poll.
      const r = await fetch(`${API}/brain/ingest/proposals?limit=200&since_minutes=1440`, { credentials: 'include' });
      const d = await r.json();
      if (!Array.isArray(d.proposals)) return;
      setProposals(d.proposals);
      // Pre-check newly-proposed rows whose confidence ≥ threshold; never re-check
      // a row the user has already toggled (autoCheckedRef tracks that).
      setChecked((prev) => {
        const next = { ...prev };
        for (const p of d.proposals) {
          if (p.status !== 'proposed') continue;
          if (autoCheckedRef.current.has(p.id)) continue;
          if (next[p.id] !== undefined) continue;
          if (
            p.confidence != null &&
            p.confidence >= threshold &&
            COMMITTABLE_ACTIONS.includes(p.proposed_action)
          ) {
            next[p.id] = true;
            autoCheckedRef.current.add(p.id);
          }
        }
        return next;
      });
    } catch (_) {
      /* swallow polling errors — UI stays on last good state */
    }
  }, [threshold]);

  // Poll while panel is open. Cheap when settled; the user's eyes are on it.
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [refresh]);

  const upload = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadError(null);
    setStatusMsg(`Uploading ${fileList.length} file(s)…`);
    const fd = new FormData();
    for (const f of fileList) fd.append('files', f, f.name);
    try {
      const r = await fetch(`${API}/brain/ingest/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await r.json();
      if (!r.ok) {
        setUploadError(d?.detail || `HTTP ${r.status}`);
        setStatusMsg(null);
      } else {
        const oversized = (d.proposals || []).filter((p) => p.error);
        const ok = (d.proposals || []).filter((p) => p.id);
        // Optimistic insertion: stub `pending` rows immediately so the user
        // sees them before the next poll. Closes the visibility gap when the
        // LLM finishes faster than the 2s poll interval. The next poll
        // overwrites these with the authoritative DB state.
        if (ok.length > 0) {
          const nowIso = new Date().toISOString();
          const stubs = ok.map((p) => ({
            id: p.id,
            source_filename: p.filename,
            source_size: p.size,
            proposed_slug: null,
            proposed_action: null,
            confidence: null,
            summary: null,
            similar_slugs: [],
            status: 'pending',
            created_at: nowIso,
            proposed_at: null,
            committed_at: null,
          }));
          setProposals((prev) => {
            const seen = new Set(prev.map((r) => r.id));
            return [...stubs.filter((s) => !seen.has(s.id)), ...prev];
          });
          // If a non-default filter is active, snap to "Needs review" so
          // the user sees their freshly-uploaded rows. They can switch back.
          if (filterKey !== 'all' && filterKey !== 'review') setFilterKey('review');
        }
        setStatusMsg(
          `${ok.length} file(s) queued. They'll appear below — pending → proposed (~3s) → committed (auto if ≥${(threshold * 100).toFixed(0)}%) or awaiting your review.` +
          (oversized.length ? ` ${oversized.length} skipped (too large).` : '')
        );
        refresh();
      }
    } catch (e) {
      setUploadError(e.message);
    }
  }, [filterKey, refresh, threshold]);

  const onFileInput = (e) => upload(e.target.files);
  const onDrop = (e) => { e.preventDefault(); upload(e.dataTransfer.files); };
  const onDragOver = (e) => e.preventDefault();

  const openDrawer = useCallback(async (id) => {
    setDrawerId(id);
    setDrawerData(null);
    try {
      const r = await fetch(`${API}/brain/ingest/proposals/${id}`, { credentials: 'include' });
      const d = await r.json();
      setDrawerData(d);
    } catch (e) {
      setDrawerData({ error: e.message });
    }
  }, []);

  const onThresholdChange = (e) => {
    const v = parseFloat(e.target.value);
    setThreshold(v);
    localStorage.setItem(THRESHOLD_KEY, String(v));
  };
  const onThresholdCommit = async () => {
    try {
      await fetch(`${API}/brain/ingest/threshold`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_commit_threshold: threshold }),
      });
    } catch (_) { /* swallow — local value still saved */ }
  };

  const toggleCheck = (id) => {
    autoCheckedRef.current.add(id); // user touched it — never auto-toggle again
    setChecked((p) => ({ ...p, [id]: !p[id] }));
  };
  const setOverride = (id, key, value) =>
    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), [key]: value } }));

  const checkedIds = useMemo(
    () => Object.entries(checked).filter(([, v]) => v).map(([k]) => parseInt(k, 10)),
    [checked]
  );

  // Status counts power both the summary banner and the filter chip badges.
  const counts = useMemo(() => {
    const c = { pending: 0, proposed: 0, committed: 0, skipped: 0, failed: 0 };
    for (const p of proposals) c[p.status] = (c[p.status] || 0) + 1;
    return c;
  }, [proposals]);

  const visible = useMemo(() => {
    const f = STATUS_FILTERS.find((x) => x.key === filterKey) || STATUS_FILTERS[0];
    return proposals.filter(f.match);
  }, [proposals, filterKey]);

  // Bulk actions only operate on rows still in `proposed`. Stale checks on
  // a row that auto-committed mid-session are silently dropped — that's
  // closer to user intent than re-committing or erroring.
  const actionableCheckedIds = useMemo(
    () => checkedIds.filter((id) => proposals.find((p) => p.id === id && p.status === 'proposed')),
    [checkedIds, proposals]
  );

  const approveChecked = useCallback(async () => {
    if (actionableCheckedIds.length === 0) return;
    setBusy(true);
    setStatusMsg('Committing…');
    try {
      const items = actionableCheckedIds.map((id) => {
        const row = proposals.find((p) => p.id === id);
        const ov = overrides[id] || {};
        return {
          id,
          slug_override: ov.slug ?? row?.proposed_slug ?? undefined,
          action_override: ov.action ?? row?.proposed_action ?? undefined,
        };
      });
      const r = await fetch(`${API}/brain/ingest/commit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposals: items }),
      });
      const d = await r.json();
      if (!r.ok) {
        setStatusMsg(`Commit failed: ${d?.detail || `HTTP ${r.status}`}`);
      } else {
        const errs = (d.results || []).filter((x) => x.error);
        const okN = (d.results || []).length - errs.length;
        setStatusMsg(`${okN} committed${errs.length ? `, ${errs.length} failed` : ''}.`);
        setChecked({});
        setOverrides({});
        refresh();
      }
    } catch (e) {
      setStatusMsg(`Commit error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }, [actionableCheckedIds, proposals, overrides, refresh]);

  const skipChecked = useCallback(async () => {
    if (actionableCheckedIds.length === 0) return;
    setBusy(true);
    setStatusMsg('Skipping…');
    try {
      const r = await fetch(`${API}/brain/ingest/skip`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_ids: actionableCheckedIds }),
      });
      const d = await r.json();
      if (!r.ok) {
        setStatusMsg(`Skip failed: ${d?.detail || `HTTP ${r.status}`}`);
      } else {
        setStatusMsg(`${d.skipped ?? actionableCheckedIds.length} skipped.`);
        setChecked({});
        refresh();
      }
    } catch (e) {
      setStatusMsg(`Skip error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }, [actionableCheckedIds, refresh]);

  return (
    <div>
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center mb-4 bg-slate-50 hover:bg-slate-100 transition"
      >
        <p className="text-sm text-slate-600 mb-2">Drop .txt or .md files here, or</p>
        <label className="inline-block bg-indigo-600 text-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer">
          Choose files
          <input
            type="file"
            multiple
            accept=".txt,.md,text/plain,text/markdown"
            onChange={onFileInput}
            className="hidden"
          />
        </label>
        {uploadError && <div className="mt-3 text-sm text-red-600">{uploadError}</div>}
      </div>

      {statusMsg && (
        <div className="text-sm text-slate-600 mb-3">{statusMsg}</div>
      )}

      {/* Activity summary — only render counts that are non-zero. The "last
          24h" suffix anchors the user's expectation about the window. */}
      {proposals.length > 0 && (
        <div className="text-sm text-slate-600 mb-2 flex flex-wrap gap-x-4 gap-y-1 items-center">
          {counts.committed > 0 && (
            <span><span className="text-emerald-700">●</span> {counts.committed} committed</span>
          )}
          {(counts.pending + counts.proposed) > 0 && (
            <span><span className="text-blue-700">●</span> {counts.pending + counts.proposed} need review</span>
          )}
          {counts.failed > 0 && (
            <span><span className="text-amber-700">●</span> {counts.failed} failed</span>
          )}
          {counts.skipped > 0 && (
            <span className="text-slate-500"><span>●</span> {counts.skipped} skipped</span>
          )}
          <span className="text-slate-400 text-xs ml-auto">last 24h</span>
        </div>
      )}

      {/* Status filter chips */}
      <div className="flex gap-1.5 mb-2 flex-wrap">
        {STATUS_FILTERS.map((f) => {
          const n =
            f.key === 'all'    ? proposals.length :
            f.key === 'review' ? counts.pending + counts.proposed :
            f.key === 'done'   ? counts.committed :
            f.key === 'failed' ? counts.failed :
                                 counts.skipped;
          const active = filterKey === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilterKey(f.key)}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition ${
                active
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {f.label}{n > 0 && <span className={active ? 'opacity-70 ml-1' : 'text-slate-400 ml-1'}>({n})</span>}
            </button>
          );
        })}
      </div>

      {/* Proposal table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden mb-24">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="w-8 px-2 py-2"></th>
              <th className="px-2 py-2 text-left">Filename</th>
              <th className="px-2 py-2 text-left">Slug</th>
              <th className="px-2 py-2 text-left">Action</th>
              <th className="px-2 py-2 text-left">Conf</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Summary</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan="7" className="px-3 py-6 text-center text-slate-400 text-sm italic">
                  {proposals.length === 0
                    ? 'No recent activity (last 24h). Drop files above to start.'
                    : `No rows match "${STATUS_FILTERS.find(f => f.key === filterKey)?.label}".`}
                </td>
              </tr>
            )}
            {visible.map((p) => {
              const isProposed = p.status === 'proposed';
              const isCommitted = p.status === 'committed';
              const isFailed = p.status === 'failed';
              const isSkipped = p.status === 'skipped';
              return (
                <tr
                  key={p.id}
                  className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${
                    isSkipped ? 'opacity-60' : ''
                  }`}
                  onClick={() => openDrawer(p.id)}
                >
                  <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!checked[p.id]}
                      onChange={() => toggleCheck(p.id)}
                      disabled={!isProposed}
                      title={isProposed ? '' : `Already ${p.status} — no action available`}
                    />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{p.source_filename}</td>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    {isCommitted ? (
                      // Final, immutable: render as a citation chip so it's
                      // visually distinct from editable proposed slugs.
                      p.proposed_slug
                        ? <CitationChip slug={p.proposed_slug} />
                        : <span className="text-slate-400 text-xs">—</span>
                    ) : isProposed ? (
                      <input
                        type="text"
                        value={overrides[p.id]?.slug ?? p.proposed_slug ?? ''}
                        onChange={(e) => setOverride(p.id, 'slug', e.target.value)}
                        placeholder="(no slug)"
                        className="w-full border border-slate-200 rounded px-1.5 py-0.5 font-mono text-xs"
                      />
                    ) : (
                      // Pending / failed / skipped: show whatever's there as static text.
                      <span className="font-mono text-xs text-slate-400">{p.proposed_slug || '—'}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5"><ActionBadge action={p.proposed_action} /></td>
                  <td className="px-2 py-1.5"><ConfidencePill value={p.confidence} /></td>
                  <td className="px-2 py-1.5"><StatusBadge status={p.status} /></td>
                  <td className="px-2 py-1.5 text-slate-700 truncate max-w-md">
                    {p.status === 'pending'
                      ? <span className="italic text-slate-400">analyzing…</span>
                      : isFailed
                        ? <span className="text-amber-700 text-xs italic">failed — click row to inspect</span>
                        : p.summary}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sticky bulk-action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-3 flex items-center justify-between gap-4 z-30">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 mr-1">
            {actionableCheckedIds.length} actionable
            {checkedIds.length > actionableCheckedIds.length && (
              <span className="text-slate-400 ml-1">
                ({checkedIds.length - actionableCheckedIds.length} already done)
              </span>
            )}
          </span>
          <button
            onClick={approveChecked}
            disabled={busy || actionableCheckedIds.length === 0}
            className="bg-emerald-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-300"
          >
            Approve checked
          </button>
          <button
            onClick={skipChecked}
            disabled={busy || actionableCheckedIds.length === 0}
            className="bg-rose-100 text-rose-800 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-rose-200 disabled:opacity-60"
          >
            Skip checked
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Auto-commit ≥</span>
          <input
            type="range"
            min="0"
            max="1.01"
            step="0.01"
            value={threshold}
            onChange={onThresholdChange}
            onMouseUp={onThresholdCommit}
            onTouchEnd={onThresholdCommit}
            className="w-32"
          />
          <span className="font-mono text-xs w-12 text-right">
            {threshold > 1 ? 'off' : `${(threshold * 100).toFixed(0)}%`}
          </span>
        </div>
      </div>

      {/* Preview drawer */}
      {drawerId !== null && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setDrawerId(null)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-900">Proposal #{drawerId}</h3>
                <button
                  onClick={() => setDrawerId(null)}
                  className="text-slate-500 hover:text-slate-900 text-xl leading-none"
                >×</button>
              </div>
              {!drawerData && <div className="text-sm text-slate-500 italic">Loading…</div>}
              {drawerData?.error && <div className="text-sm text-red-600">{drawerData.error}</div>}
              {drawerData && !drawerData.error && (
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[11px] font-semibold uppercase text-slate-500">Filename</div>
                    <div className="font-mono">{drawerData.source_filename}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase text-slate-500">Action / Slug</div>
                    <div>
                      <ActionBadge action={drawerData.proposed_action} />
                      <span className="font-mono ml-2">{drawerData.proposed_slug || '(none)'}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase text-slate-500">Reasoning</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{drawerData.reasoning || '(none)'}</div>
                  </div>
                  {Array.isArray(drawerData.similar_slugs) && drawerData.similar_slugs.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase text-slate-500">Similar pages</div>
                      <div>{drawerData.similar_slugs.map((s) => <CitationChip key={s} slug={s} />)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[11px] font-semibold uppercase text-slate-500">File content</div>
                    <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 max-h-96 overflow-auto whitespace-pre-wrap font-mono">{drawerData.source_content}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* "Correct this" — Slice 5 friction-killer. Drops a synthetic correction
 * file into the same ingest queue as drag-drop. LLM proposes update/append
 * against the named slug. */
function CorrectModal({ open, onClose, defaultSlug, defaultQuestion, onSubmitted }) {
  const [slug, setSlug] = useState(defaultSlug || '');
  const [question, setQuestion] = useState(defaultQuestion || '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setSlug(defaultSlug || '');
      setQuestion(defaultQuestion || '');
      setText('');
      setErr(null);
      setBusy(false);
    }
  }, [open, defaultSlug, defaultQuestion]);

  if (!open) return null;

  const submit = async () => {
    if (!text.trim()) { setErr('Tell us what should be different.'); return; }
    setBusy(true); setErr(null);
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `correction-${ts}.md`;
    const tgtLine = slug.trim() ? `correction_target: ${slug.trim()}\n` : '';
    const heading = slug.trim() ? `# Correction for [${slug.trim()}]` : '# Correction';
    const qBlock = question.trim() ? `**Original question:** ${question.trim()}\n\n` : '';
    const content =
`---
type: correction
${tgtLine}created: ${new Date().toISOString()}
tags: [knowledge-ingest, correction]
---

${heading}

${qBlock}**What's wrong / what should it say:**

${text.trim()}
`;
    try {
      const r = await fetch(`${API}/brain/ingest/upload`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: [{ filename, content }] }),
      });
      const d = await r.json();
      if (!r.ok) {
        setErr(d?.detail || `HTTP ${r.status}`);
        setBusy(false);
        return;
      }
      onSubmitted();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-900">Correct this</h3>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-xl leading-none">×</button>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Drops a correction note into the Ingest queue. The LLM will propose
            an update or append against the slug below.
          </p>
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-slate-500 mt-3 mb-1">Target slug</label>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. projects/foo-bar"
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"
          />
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-slate-500 mt-3 mb-1">Original question</label>
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
          />
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-slate-500 mt-3 mb-1">What's wrong / what should it say</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
            placeholder="Free-text. Be specific."
          />
          {err && <div className="mt-2 text-sm text-red-600">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-50">Cancel</button>
            <button onClick={submit} disabled={busy || !text.trim()} className="px-3 py-1.5 rounded-md text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300">
              {busy ? 'Submitting…' : 'Submit correction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function Knowledge() {
  const [modeKey, setModeKey] = useState('ask');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [health, setHealth] = useState(null);
  const [correctOpen, setCorrectOpen] = useState(false);

  const mode = MODES.find((m) => m.key === modeKey);

  // Health badge — runs once on mount.
  useEffect(() => {
    fetch(`${API}/brain/health`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setHealth(d))
      .catch(() => setHealth({ status: 'unreachable' }));
  }, []);

  const submit = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      let url = `${API}${mode.path}`;
      let opts = { method: mode.method, credentials: 'include' };
      if (mode.method === 'GET') {
        if (modeKey === 'brief') {
          if (!query.trim()) { setError('Topic is required.'); setLoading(false); return; }
          url += `?topic=${encodeURIComponent(query)}`;
        }
        // 'today' takes no query — already wired.
      } else {
        if (!query.trim()) { setError('Question is required.'); setLoading(false); return; }
        opts.headers = { 'Content-Type': 'application/json' };
        // limit=12 to give the recall-api fanout retrieval room to surface
        // breadth pages alongside the top-1 dense-match. limit=6 was hiding
        // the platform-overview pages on natural-language queries.
        opts.body = JSON.stringify({ query, limit: 12 });
      }
      const r = await fetch(url, opts);
      const data = await r.json();
      if (!r.ok) {
        setError(data?.detail || `HTTP ${r.status}`);
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mode, modeKey, query]);

  // Auto-fire 'today' on mode switch since it has no input.
  useEffect(() => {
    if (modeKey === 'today') submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKey]);

  const answer =
    result?.answer ?? result?.brief ?? result?.note ?? result?.briefing ?? null;
  const sources = result?.sources?.page_slugs || [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Knowledge</h1>
          <p className="text-sm text-slate-500">
            Search and synthesize across the brain. Powered by Total Recall.
          </p>
        </div>
        {health && (
          <span
            className={`text-[11px] font-mono px-2 py-1 rounded ${
              health.status === 'ok'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
            title={JSON.stringify(health)}
          >
            Total Recall: {health.status || 'unknown'}
          </span>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        {MODES.map((m) => (
          <Mode
            key={m.key}
            mode={m}
            active={m.key === modeKey}
            onClick={() => { setModeKey(m.key); setResult(null); setError(null); }}
          />
        ))}
      </div>

      {modeKey === 'ingest' ? (
        <IngestPanel />
      ) : (
        <>
          {modeKey !== 'today' && (
            <div className="flex gap-2 mb-6">
              <input
                type="text"
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={mode.placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !loading) submit(); }}
                disabled={loading}
              />
              <button
                onClick={submit}
                disabled={loading || !query.trim()}
                className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-300"
              >
                {loading ? 'Thinking…' : 'Go'}
              </button>
            </div>
          )}

          {loading && (
            <div className="text-sm text-slate-500 italic mb-4">
              {modeKey === 'today' ? 'Pulling the last 24 hours of brain activity…' : 'Querying brain + synthesizing…'}
            </div>
          )}

          {error && (
            <div className="border border-red-200 bg-red-50 text-red-700 rounded-md px-4 py-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {result && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {answer && (
                <div className="px-5 py-4 bg-white">
                  <div className="prose prose-sm max-w-none text-slate-800 whitespace-pre-wrap">{answer}</div>
                </div>
              )}
              {sources.length > 0 && (
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Sources</div>
                  <div>{sources.map((s) => <CitationChip key={s} slug={s} />)}</div>
                </div>
              )}
              {!answer && (
                <div className="px-5 py-4 bg-white text-sm text-slate-500">
                  <pre className="text-xs overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
              <div className="px-5 py-2 bg-white border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setCorrectOpen(true)}
                  className="text-[11px] text-slate-500 hover:text-indigo-700 underline decoration-dotted"
                  title="Submit a correction — drops a note into the Ingest queue"
                >
                  Correct this
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <CorrectModal
        open={correctOpen}
        onClose={() => setCorrectOpen(false)}
        defaultSlug={sources[0] || ''}
        defaultQuestion={query}
        onSubmitted={() => {
          setCorrectOpen(false);
          setModeKey('ingest');
        }}
      />
    </div>
  );
}
